import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Select, Option, Menu, MenuHandler, MenuList, MenuItem, Button, Input, Alert } from '@material-tailwind/react'
import { InformationIcon, SuccessIcon, WarningIcon } from '../Icons';
import { Dialog, Transition } from '@headlessui/react';

export default function SettingsModal({ resetFlag, updateSettings, open, handler }) {
  const defaultAlert = {
    mode: "info",
    message: "Change upload settings here",
  }
  const fullUrl = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}${window.location.pathname}${window.location.search}${window.location.hash}`;
  
  const [alert, setAlert] = useState(defaultAlert);
  const [expiryUnit, setExpiryUnit] = useState(localStorage.getItem("expiryUnit") || "days");
  const [expiryValue, setExpiryValue] = useState(localStorage.getItem("expiryValue") || 1);
  const [customUrl, setCustomUrl] = useState(localStorage.getItem("customUrl") || "");
  const [password, setPassword] = useState("");
  const expiresRef = useRef(null);
  const alertRef = useRef(null);

  useEffect(() => {
    if (!resetFlag) return;
    
    setExpiryUnit("days");
    setExpiryValue(1);
    setCustomUrl("");
    setPassword("");
  }, [resetFlag])
  
  useEffect(validateExpiryTime, [expiryUnit]);
  useEffect(saveToLocalStorage, [expiryValue, expiryUnit, customUrl])

  useEffect(() => {
    const { unitToSeconds } = getUnitConfig();
    const settings = {
      userExpiry: expiryValue * unitToSeconds[expiryUnit],
      userCustomUrl: customUrl,
      userPassword: password,
    }
    updateSettings(settings)
  }, [expiryUnit, expiryValue, customUrl, password])
  
  function revertAlert(timeout) {
    if (alertRef.current) clearInterval(alertRef.current);
    alertRef.current = setTimeout(() => {
      setAlert(defaultAlert)
    }, timeout)
  }

  function displayAlert(mode, message, timeout = 2500) {
    setAlert({
      mode: mode,
      message: message,
    })
    revertAlert(timeout)
  }

  function getUnitConfig() {
    const unitToSeconds = {
      seconds: 1,
      minutes: 60,
      hours: 3600,
      days: 86400,
    };
  
    const unitToMaxValue = {
      seconds: 604800,
      minutes: 10080,
      hours: 168,
      days: 7,
    };
  
    return { unitToSeconds, unitToMaxValue };
  }

  function handleChangeExpiryValue(value) {
    if (value === "") {
      setExpiryValue(value);
      return
    }

    const numericValue = Number(value);

    if (isNaN(numericValue) || numericValue <= 0) {
      displayAlert("error", `Expiry time cannot be less than 1 second`);
      return;
    }
  
    const { unitToMaxValue } = getUnitConfig();
  
    if (!unitToMaxValue.hasOwnProperty(expiryUnit)) {
      displayAlert("error", "Unsupported time unit");
      return;
    }
  
    if (numericValue > unitToMaxValue[expiryUnit]) {
      displayAlert("error", `Expiry time cannot be more than ${unitToMaxValue[expiryUnit]} ${expiryUnit}`);
      setExpiryValue(unitToMaxValue[expiryUnit]);
      return;
    } else {
      setExpiryValue(numericValue);
    }
  }
  
  function validateExpiryTime() {
    if (!expiryValue || isNaN(expiryValue) || !expiryUnit) {
      setExpiryValue(1);
      return;
    }
  
    const { unitToSeconds, unitToMaxValue } = getUnitConfig();
  
    if (!unitToSeconds[expiryUnit]) {
      displayAlert("error", "Unsupported time unit");
      return;
    }
  
    const valueInSeconds = parseFloat(expiryValue) * unitToSeconds[expiryUnit];
    const maxSeconds = unitToMaxValue["seconds"];
  
    if (valueInSeconds > maxSeconds) {
      displayAlert("error", `Expiry time cannot be more than ${unitToMaxValue[expiryUnit]} ${expiryUnit}`);
      setExpiryValue(maxSeconds / unitToSeconds[expiryUnit]);
    }
  }
  
  function handleChangeCustomUrl(value) {
    if (!value.includes(fullUrl)) {
      setCustomUrl("");
      return;
    }

    const custom = value.replace(fullUrl, "")
    
    if (custom === "") {
      setCustomUrl("");
      return;
    }

    if (custom.length > 15) {
      displayAlert("error", "Custom URL must not be more than 15 characters");
      return;
    }

    if (/[^A-Za-z0-9]/.test(custom)) {
      displayAlert("error", "Custom URL must be alphanumeric");
      return;
    }

    setCustomUrl(custom)
  }

  function validateCustomUrl() {
    if (customUrl.length === 0) return;

    if (customUrl.length < 4) {
      setCustomUrl("");
      displayAlert("error", "Minimum length of custom URL is 4");
      return
    }

    if (customUrl.length > 15) {
      setCustomUrl("");
      displayAlert("error", "Maximum length of custom URL is 15");
      return
    }

    if (/[^A-Za-z0-9]/.test(customUrl)) {
      setCustomUrl("");
      displayAlert("error", "Custom URL must be alphanumeric");
      return
    }
  }

  function saveToLocalStorage() {
    localStorage.setItem("expiryUnit", expiryUnit);
    localStorage.setItem("expiryValue", expiryUnit);
    localStorage.setItem("customUrl", customUrl);
  }

  // TODO: Visibility: public, unlisted, private (only for logged in)
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" initialFocus={expiresRef} className="relative z-10" onClose={handler}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-75"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-75"
            >
              <Dialog.Panel className="w-[90%] lg:max-w-[40%] transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-2xl font-semibold leading-6 text-gray-900 mb-4 flex justify-between items-center"
                >
                  <span>Settings</span>
                  <i onClick={handler} className='bx bx-x cursor-pointer'></i>
                </Dialog.Title>
                  <div className='w-full px-4 py-0'>
                    <Alert 
                      icon={alert.mode === "success" ? <SuccessIcon /> : alert.mode === "error" ? <WarningIcon   /> : <InformationIcon />} 
                      variant='outlined'
                      className={
                        `${alert.mode === "success" ? "rounded-none border-l-4 border-[#2ec946] bg-[#2ec946]/10 font-medium text-[#2ec946]" : 
                          alert.mode === "error" ? "rounded-none border-l-4 border-red-600 bg-red-100/10 font-medium text-red-300" : 
                          "text-gray-900"} 
                        py-3 settings-alert`}
                    >
                      {alert.message}
                    </Alert>
                    <div className='w-full flex flex-col my-4'>
                      <span>Expires in</span>
                      <div className=" flex flex-row-reverse w-full">
                        <Menu placement="bottom">
                          <MenuHandler>
                            <Button
                              ripple={false}
                              variant="text"
                              color="white"
                              className="flex h-10 w-5/12 items-center gap-2 rounded-l-none border border-l-0 border-blue-gray-200 bg-blue-gray-900 hover:bg-black active:bg-black pl-3 outline-none"
                            >
                              {expiryUnit}
                            </Button>
                          </MenuHandler>
                          <MenuList className="max-h-[20rem] max-w-[18rem] z-[10000]">
                            {["days", "hours", "minutes", "seconds"].map(timeUnit => (
                              <MenuItem key={timeUnit} onClick={() => setExpiryUnit(timeUnit)} className={`${expiryUnit === timeUnit ? "bg-blue-gray-50 bg-opacity-80 text-blue-gray-900" : ""} flex items-center gap-2`}>
                                {timeUnit[0].toUpperCase() + timeUnit.substring(1)}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                        <Input
                          ref={expiresRef}
                          type="number"
                          value={expiryValue}
                          onChange={e => handleChangeExpiryValue(e.target.value)}
                          onBlur={validateExpiryTime}
                          className="text-lg rounded-r-none !border-blue-gray-200"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          containerProps={{
                            className: "!min-w-0",
                          }}
                        />
                      </div>
                    </div>

                    <div className='mb-4'>
                      <span>Custom URL</span>
                      <div className='flex flex-col'>
                        <Input 
                          type="text"
                          value={fullUrl + customUrl}
                          onChange={e => handleChangeCustomUrl(e.target.value)}
                          onBlur={validateCustomUrl}
                          className='w-full px-3 py-2.5 border !border-blue-gray-200 border-solid rounded-[7px]'
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          containerProps={{
                            className: "!min-w-0",
                          }}
                        />
                      </div>
                      <span className='text-xs text-gray-500'>Leave blank for a Random URL</span>
                    </div>
                    <div className='mb-4'>
                      <span>Set Password</span>
                      <Input 
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className='w-full px-3 py-2.5 border !border-blue-gray-200 border-solid rounded-[7px]'
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                        containerProps={{
                          className: "!min-w-0",
                        }}
                      />
                      <span className='text-xs text-gray-500'>Leave blank for no password</span>
                    </div>
                </div>
                <div className='pt-1 px-4 mb-3'>
                  <div className='w-full flex flex-col sm:flex-row gap-2 sm:gap-5'>
                    <Button onClick={handler} variant='outlined' className='w-full'>Back</Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
